export default `uniform float delta;
uniform vec3 uObstacles[7]; 
uniform float uObstacleRadius; 
uniform float uAvoidanceSamples;
uniform float uAvoidanceWeight;
uniform float perceptionRadius;
uniform float separationRadius;
uniform float maxSpeed;
uniform float maxForce;
uniform float separationWeight;
uniform float alignmentWeight;
uniform float cohesionWeight;
uniform float originForceWeight;
uniform float boundaryForceWeight;
uniform float wanderWeight;
uniform float wanderDistance;
uniform float wanderRadius;
uniform float wanderSpeed;
uniform float uTimeB;
uniform vec3 boundsMin;
uniform vec3 boundsMax;

void main() {
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec3 myPos = texture2D(texturePosition, uv).xyz;
    vec3 myVel = texture2D(textureVelocity, uv).xyz;

    vec3 acc = vec3(0.0);
    vec3 sep = vec3(0.0);
    vec3 ali = vec3(0.0);
    vec3 coh = vec3(0.0);

    float flockCount = 0.0;
    float sepCount = 0.0;

    float pRadSq = perceptionRadius * perceptionRadius;
    float sRadSq = separationRadius * separationRadius;

    for (float y = 0.0; y < resolution.y; y++) {
        for (float x = 0.0; x < resolution.x; x++) {
            vec2 otherUv = vec2(x + 0.5, y + 0.5) / resolution.xy;
            if (otherUv == uv) continue;

            vec3 otherPos = texture2D(texturePosition, otherUv).xyz;
            vec3 otherVel = texture2D(textureVelocity, otherUv).xyz;

            vec3 diff = myPos - otherPos;
            float distSq = dot(diff, diff);

            if (distSq < pRadSq) {
                flockCount += 1.0;
                ali += otherVel;
                coh += otherPos;

                if (distSq < sRadSq && distSq > 0.0001) {
                    float dist = sqrt(distSq);
                    sep += normalize(diff) / dist;
                    sepCount += 1.0;
                }
            }
        }
    }

    
    if (flockCount > 0.0) {
        ali /= flockCount;
        
        ali = normalize(ali) * maxSpeed;
        ali -= myVel;
        ali = clamp(ali, vec3(-maxForce), vec3(maxForce));

        coh /= flockCount;
        
        coh -= myPos;
        coh = normalize(coh) * maxSpeed;
        coh -= myVel;
        coh = clamp(coh, vec3(-maxForce), vec3(maxForce));

        acc += ali * alignmentWeight;
        acc += coh * cohesionWeight;
    }
    if (sepCount > 0.0) {
        sep /= sepCount;
        sep = normalize(sep) * maxSpeed;
        sep -= myVel;
        sep = clamp(sep, vec3(-maxForce), vec3(maxForce));
        acc += sep * separationWeight;
    }

    
    
    float distToOrigin = length(myPos);
    float seekStrength = pow(max(0.0, (distToOrigin - 20.0) / 50.0), 2.0);
    vec3 seek = normalize(-myPos) * seekStrength * originForceWeight;
    acc += seek;

    
    float floor = boundsMin.y + 5.0;
    float ceiling = boundsMax.y - 5.0;
    vec3 boundary = vec3(0.0);
    if (myPos.y < floor) {
        boundary.y = sqrt(floor - myPos.y);
    } else if (myPos.y > ceiling) {
        boundary.y = sqrt(myPos.y - ceiling) * -1.0;
    }
    acc += boundary * boundaryForceWeight;

    
    vec3 forward = normalize(myVel);
    if(length(myVel) < 0.0001) forward = vec3(1.0, 0.0, 0.0);
    
    float angle1 = uTimeB * wanderSpeed + (uv.x * 6.28 + uv.y * 13.13);
    float angle2 = uTimeB * wanderSpeed * 0.73 + (uv.y * 6.28 + uv.x * 17.17);
    
    vec3 wanderOffset = vec3(
        cos(angle1) * cos(angle2),
        sin(angle1) * cos(angle2),
        sin(angle2)
    ) * wanderRadius;
    
    vec3 wanderTarget = myPos + forward * wanderDistance + wanderOffset;
    vec3 wanderSteer = normalize(wanderTarget - (myPos + myVel)) * maxSpeed;
    wanderSteer -= myVel;
    acc += clamp(wanderSteer, vec3(-maxForce), vec3(maxForce)) * wanderWeight;

    
    vec3 avoidanceForce = vec3(0.0);
    int obstacleIndex = -1;
    bool isHitting = false;

    
    for (int i = 0; i < 7; i++) {
        vec3 L = uObstacles[i] - myPos;
        float d = length(L);
        vec3 dir = normalize(myVel);
        if (length(myVel) < 0.0001) dir = vec3(1.0, 0.0, 0.0);
        float tca = dot(L, dir);
        
        
        if (d < uObstacleRadius + 2.0 || (tca > 0.0 && d < uObstacleRadius + 10.0)) {
            float d2 = dot(L, L) - tca * tca;
            if (d < uObstacleRadius + 2.0 || d2 < (uObstacleRadius + 1.0) * (uObstacleRadius + 1.0)) {
                isHitting = true;
                obstacleIndex = i;
                break;
            }
        }
    }

    if (isHitting) {
        vec3 forward = normalize(myVel);
        if (length(myVel) < 0.0001) forward = vec3(1.0, 0.0, 0.0);
        
        vec3 tempUp = abs(forward.y) > 0.99 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);
        vec3 right = normalize(cross(tempUp, forward));
        vec3 up = cross(forward, right);

        vec3 bestDir = forward;
        bool found = false;

        
        for (float i = 1.0; i <= 64.0; i++) {
            if (i > uAvoidanceSamples) break;

            float t = i / uAvoidanceSamples;
            float angle = t * 2.5; 
            float phi = i * 2.39996; 

            vec3 offset = (right * cos(phi) + up * sin(phi)) * sin(angle);
            vec3 sampleDir = normalize(forward * cos(angle) + offset);

            bool sampleHits = false;
            
            for (int j = -1; j <= 1; j++) {
                int idx = (obstacleIndex + j + 7) % 7;
                vec3 L_j = uObstacles[idx] - myPos;
                float tca_j = dot(L_j, sampleDir);
                
                if (tca_j > 0.0) {
                    float d2_j = dot(L_j, L_j) - tca_j * tca_j;
                    if (d2_j < (uObstacleRadius + 1.5) * (uObstacleRadius + 1.5)) {
                        sampleHits = true;
                        break;
                    }
                }
                
                if (length(L_j) < uObstacleRadius + 1.0 && tca_j > 0.0) {
                    sampleHits = true;
                    break;
                }
            }

            if (!sampleHits) {
                bestDir = sampleDir;
                found = true;
                break;
            }
        }

        if (found) {
            vec3 desired = bestDir * maxSpeed;
            vec3 steer = desired - myVel;
            avoidanceForce = clamp(steer, vec3(-maxForce), vec3(maxForce)) * uAvoidanceWeight;
        } else {
            
            vec3 steerAway = normalize(myPos - uObstacles[obstacleIndex]) * maxSpeed;
            vec3 steer = steerAway - myVel;
            avoidanceForce = clamp(steer, vec3(-maxForce), vec3(maxForce)) * uAvoidanceWeight;
        }
    }
    acc += avoidanceForce;

    
    
    acc.y *= 0.5;

    vec3 newVel = myVel + acc * (delta / 16.0);
    newVel.y *= 0.995; 

    if (length(newVel) > maxSpeed) {
        newVel = normalize(newVel) * maxSpeed;
    }

    gl_FragColor = vec4(newVel, 1.0);
}`